import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { creators as FeesActions } from '../../../Reducers/Reporting/Fees';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import { financialMonth } from '../../../Utils/Functions';
import YearSelector from '../../../Common/Selectors/YearSelector';
import appdata from '../../../Services/appdata';
import TopChart from './TopChart';
import MiddleChart from './MiddleChart';
import BottomChart from './BottomChart';

const Fees = ({  
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,
  selectedCompany,
  feesSetting,
  bubbleFlag,
}) => {
  const dispatch = useDispatch();

  // --- Safe Redux selectors (with defaults) ---
  const feeSummary = useSelector(
    (s) => s?.Reporting?.Summary?.reportingSummaries?.fees ?? ''
  );
  const summaryData = useSelector(
    (s) => s?.Reporting?.fees?.summaryData ?? {}
  );
  const detailData = useSelector(
    (s) => (Array.isArray(s?.Reporting?.fees?.detailData) ? s.Reporting.fees.detailData : [])
  );
  const lastFetchedCompany = useSelector(
    (s) => s?.Reporting?.Default?.defaultCompany ?? null
  );
  const lastFetchedDimDate = useSelector(
    (s) => s?.Reporting?.Default?.defaultDimDate ?? null
  );
  
  // --- Local state ---
  const [resize, setResize] = useState(false);
  const [selectedYears, setSelectedYears] = useState([defaultYear]);
  const [label, setLabel] = useState(String(defaultYear));
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedMiddleItems, setSelectedMiddleItems] = useState([]);
  const [filterName, setFilterName] = useState('Director');
  const [middleHeight, setMiddleHeight] = useState(137);

  // --- Callbacks ---
  const onResize = useCallback(() => setResize((p) => !p), []);

  const getFeesSummary = useCallback(
    (years, dateKeys) => dispatch(FeesActions.feesSummaryRequest(years, dateKeys)),
    [dispatch]
  );

  const getFeesDetail = useCallback(
    (years, dateKeys) => dispatch(FeesActions.feesDetailRequest(years, dateKeys)),
    [dispatch]
  );

  const reportSettingUpdateRequest = useCallback(
    (update_setting) => dispatch(ReportSettingActions.reportSettingUpdateRequest(update_setting)),
    [dispatch]
  );

  const handleFilter = useCallback(
    async (event) => {
      if (event.selectedYears !== undefined) {
        setSelectedYears(event.selectedYears);
        if (event.label !== undefined) setLabel(event.label);
      }
      if (event.selectedMonths !== undefined) {
        setSelectedMonths(event.selectedMonths);
      }
      if (event.selectedTopItems !== undefined) {
        setSelectedTopItems(event.selectedTopItems);
      }
      if (event.selectedMiddleItems !== undefined) {
        setSelectedMiddleItems(event.selectedMiddleItems);
      }
      if (event.filterName !== undefined) {
        setFilterName(event.filterName);
      }

      if (Array.isArray(event.selectedTopItems) && event.selectedTopItems.length > 0) {
        const dimDateKeys = event.selectedTopItems.map(
          (d) => `${d.year}${String((d.month ?? 0) + 1).padStart(2, '0')}01`
        );
        appdata.setLocalItem('feeTopItems', event.selectedTopItems);
        await getFeesDetail([defaultYear], dimDateKeys);
      }

      if (event.selectedMiddleItems || event.filterName) {
        if (Array.isArray(event.selectedMiddleItems)) {
          appdata.setLocalItem('feeMiddleItems', event.selectedMiddleItems);
        }
        await reportSettingUpdateRequest({
          Fees: JSON.stringify({
            filterName: event.filterName || filterName,
          }),
        });
      }
    },
    [defaultYear, filterName, getFeesDetail, reportSettingUpdateRequest]
  );

  const handleYear = useCallback(
    async (event) => {
      if (!event?.selectedYears?.length) {
        toast.warning('Please select the year.');
        return;
      }
      getFeesSummary(event.selectedYears, [defaultDimDate]);
      getFeesDetail(event.selectedYears, [defaultDimDate]);
      handleFilter(event);
    },
    [defaultDimDate, getFeesSummary, getFeesDetail, handleFilter]
  );

  const getMiddleChartHeight = useCallback((height) => {
    setMiddleHeight(height);
  }, []);

  // --- Mount: seed UI state & listeners ---
  useEffect(() => {
    const initialSelectedYears = [defaultYear];
    const initialSelectedMonths = [financialMonth(defaultMonth, defaultStartMonth) - 1];
    const initialSelectedTopItems =
      appdata.getLocalItem('feeTopItems') ||
      [
        {
          year: Math.floor(parseInt(defaultDimDate, 10) / 10000),
          month: financialMonth(defaultMonth, defaultStartMonth) - 1,
        },
      ];
    const initialSelectedMiddleItems = appdata.getLocalItem('feeMiddleItems') || [];

    setLabel(String(defaultYear));
    setSelectedYears(initialSelectedYears);
    setSelectedMonths(initialSelectedMonths);
    setSelectedTopItems(initialSelectedTopItems);
    setSelectedMiddleItems(initialSelectedMiddleItems);

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerFeesRequests = useCallback(() => {
    getFeesSummary([defaultYear], [defaultDimDate]);
    getFeesDetail([defaultYear], [defaultDimDate]);
  }, [defaultYear, defaultDimDate, getFeesSummary, getFeesDetail]);

  // --- Respond to prop changes (FY/month/dimdate/setting) ---
  useEffect(() => {
    const run = async () => {
      const newSelectedYears = [defaultYear];
      const newSelectedMonths = [financialMonth(defaultMonth, defaultStartMonth) - 1];
      const newSelectedTopItems = [
        {
          year: Math.floor(parseInt(defaultDimDate, 10) / 10000),
          month: financialMonth(defaultMonth, defaultStartMonth) - 1,
        },
      ];
      const newSelectedMiddleItems = [];

      setSelectedYears(newSelectedYears);
      setSelectedMonths(newSelectedMonths);
      setSelectedTopItems(newSelectedTopItems);
      setSelectedMiddleItems(newSelectedMiddleItems);

      appdata.setLocalItem('feeTopItems', newSelectedTopItems);
      appdata.setLocalItem('feeMiddleItems', newSelectedMiddleItems);

      // feesSetting can be a string or object; parse safely
      try {
        let parsed = null;
        if (typeof feesSetting === 'string' && feesSetting.trim()) {
          parsed = JSON.parse(feesSetting);
        } else if (feesSetting && typeof feesSetting === 'object') {
          parsed = feesSetting;
        }
        if (parsed?.filterName) {
          await reportSettingUpdateRequest({
            Fees: JSON.stringify({ filterName: parsed.filterName }),
          });
          setFilterName(parsed.filterName);
        }
      } catch {
        // ignore bad JSON
      }
      
      if (!lastFetchedCompany || !lastFetchedDimDate) {
        triggerFeesRequests();
      }
    };
    run();
  }, [
    defaultYear,
    defaultMonth,
    defaultStartMonth,
    defaultDimDate,
    feesSetting,
    lastFetchedCompany,
    lastFetchedDimDate,
    triggerFeesRequests,
  ]);

  useEffect(() => {
    const run = async () => {
      const valuesChanged =
        lastFetchedCompany !== selectedCompany ||
        lastFetchedDimDate !== defaultDimDate;

      if (valuesChanged && lastFetchedCompany && lastFetchedDimDate) {
        triggerFeesRequests();
      }
    }

    run();
  }, [lastFetchedCompany, lastFetchedDimDate, defaultYear, defaultMonth, defaultDimDate, selectedCompany, triggerFeesRequests]);

  // --- Derived for layout ---
  const safeDetail = Array.isArray(detailData) ? detailData : [];
  const projectNameLengths = safeDetail
    .map((d) => (d?.ProjectName ? d.ProjectName.length : 0))
    .filter((n) => Number.isFinite(n) && n > 0);
  const maxNameLength = projectNameLengths.length ? Math.max(...projectNameLengths) : 0;

  return (
    <div style={{ height: '89vh' }}>
      <div className="wrapper" id="fees_year_selector">
        <YearSelector selectedYears={selectedYears} label={label} onChange={handleYear} />
        <div className="right well" />
      </div>

      <div>
        <TopChart
          summaryData={summaryData}
          selectedYears={selectedYears}
          selectedMonths={selectedMonths}
          selectedTopItems={selectedTopItems}
          defaultStartMonth={defaultStartMonth}
          handleFilter={handleFilter}
        />

        <MiddleChart
          detailData={safeDetail}
          selectedYears={selectedYears}
          selectedMonths={selectedMonths}
          selectedTopItems={selectedTopItems}
          selectedMiddleItems={selectedMiddleItems}
          filterName={filterName}
          maxNameLength={maxNameLength}
          handleFilter={handleFilter}
          getMiddleChartHeight={getMiddleChartHeight}
        />

        <BottomChart
          detailData={safeDetail}
          selectedYears={selectedYears}
          selectedMonths={selectedMonths}
          selectedTopItems={selectedTopItems}
          selectedMiddleItems={selectedMiddleItems}
          defaultStartMonth={defaultStartMonth}
          defaultMonth={defaultMonth}
          filterName={filterName}
          middleHeight={middleHeight}
          maxNameLength={maxNameLength}
        />
      </div>

      {bubbleFlag && !!feeSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
        >
          <Tooltip
            title={feeSummary}
            placement="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                },
              },
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

Fees.propTypes = {
  selectedCompany: PropTypes.number.isRequired,
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  feesSetting: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  bubbleFlag: PropTypes.bool,
};

Fees.defaultProps = {
  feesSetting: null,
  bubbleFlag: false,
};

export default Fees;
